/**
 * Per-user opt-in preference for browser turn notifications.
 *
 * Stored in localStorage so it survives page reloads but stays
 * device-local — a user who enables notifications on their laptop
 * doesn't get them on their phone.
 *
 * Default: OFF. The user has to explicitly opt in via the toggle
 * on the account page (which also handles the permission prompt).
 */

const KEY = 'ecg.turn-notifications.enabled';

export function isTurnNotificationsEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

export function setTurnNotificationsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(KEY, enabled ? '1' : '0');
  } catch {
    // localStorage may be blocked (private mode, settings); silently
    // fail — the worst that happens is the user has to re-toggle.
  }
}

/**
 * True only if the runtime supports Notifications. Used to hide
 * the toggle on environments that don't have the API (very old
 * browsers, some embedded webviews).
 */
export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export type PermissionState = 'unsupported' | NotificationPermission;

export function getPermissionState(): PermissionState {
  if (!notificationsSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Asks the browser for permission. Returns the resulting state.
 * Safe to call repeatedly — once denied, the browser remembers
 * and won't prompt again until the user clears site data.
 */
export async function requestPermission(): Promise<PermissionState> {
  if (!notificationsSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return await Notification.requestPermission();
}
